import { useEffect, useRef, useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * Contêiner com rolagem horizontal e uma barra espelhada no TOPO, para tabelas
 * largas: sem ela, a barra só aparece depois de rolar a página até o fim.
 * As duas barras andam juntas.
 */
export const ScrollHorizontal = ({ children, className }: { children: ReactNode; className?: string }) => {
  const topo = useRef<HTMLDivElement>(null);
  const corpo = useRef<HTMLDivElement>(null);
  const [largura, setLargura] = useState(0);
  const [transborda, setTransborda] = useState(false);

  useEffect(() => {
    const el = corpo.current;
    if (!el) return;
    const medir = () => {
      setLargura(el.scrollWidth);
      setTransborda(el.scrollWidth > el.clientWidth + 1);
    };
    medir();
    const obs = new ResizeObserver(medir);
    obs.observe(el);
    if (el.firstElementChild) obs.observe(el.firstElementChild);
    return () => obs.disconnect();
  }, []);

  // Evita o eco: rolar um lado move o outro, que dispararia o evento de volta.
  const origem = useRef<"topo" | "corpo" | null>(null);
  const sincronizar = (de: "topo" | "corpo") => {
    if (origem.current && origem.current !== de) return;
    origem.current = de;
    const [a, b] = de === "topo" ? [topo.current, corpo.current] : [corpo.current, topo.current];
    if (a && b) b.scrollLeft = a.scrollLeft;
    requestAnimationFrame(() => (origem.current = null));
  };

  return (
    <div className={className}>
      {transborda && (
        <div
          ref={topo}
          onScroll={() => sincronizar("topo")}
          className="mb-1 overflow-x-auto overflow-y-hidden"
          aria-hidden
        >
          <div style={{ width: largura, height: 1 }} />
        </div>
      )}
      <div ref={corpo} onScroll={() => sincronizar("corpo")} className={cn("overflow-x-auto rounded-md border")}>
        {children}
      </div>
    </div>
  );
};
